/* eslint-disable @typescript-eslint/naming-convention */
import { useState, useEffect, useContext } from 'react'
import { useRouter } from 'next/router'
import dynamic from 'next/dynamic'
import _omit from 'lodash/omit'
import _pick from 'lodash/pick'
import FilterDropdownWrapper from 'common/FilterDropdownWrapper'
import { modifyURLQuery } from 'utils/common'
import styles from '../index.module.scss'
import { SortFilterKeys } from 'app/ProviderSearch/constants'
import { MobileFilterModalContext } from 'context/ProviderSearch/MobileFilterModalProvider'
import useBreakpoint from 'hooks/useBreakPoint'
import { ProviderFilterTypes } from 'dataSource/ProviderSearch'
import { appointmentModeValueMapping } from 'app/Bookings/constants'

const SingleSelectDropdown = dynamic(
  () => import('common/SingleSelectDropdown')
)

type AppointmentModeProps = {
  allAppointmentModes: ProviderFilter[]
}

const AppointmentModeFilter: React.FC<AppointmentModeProps> = ({
  allAppointmentModes,
}) => {
  const router = useRouter()
  const { query } = router
  const [showDropdown, setShowDropdown] = useState<boolean>(false)
  const [
    selectedAppointmentMode,
    setSelectedAppointmentMode,
  ] = useState<string>('')
  const { openMobileFilter } = useContext(MobileFilterModalContext)
  const { isMobileScreen, isLargeSize } = useBreakpoint()

  useEffect(() => {
    const appointmentModeIdInQuery = _pick(query, 'availabilityType[]')[
      'availabilityType[]'
    ]

    if (!appointmentModeIdInQuery) {
      if (selectedAppointmentMode) {
        setSelectedAppointmentMode('')
      }

      return
    }

    const filteredAppointmentMode = allAppointmentModes.find(
      (appointmentMode) => appointmentMode.id === appointmentModeIdInQuery
    ).value

    setSelectedAppointmentMode(
      appointmentModeValueMapping[filteredAppointmentMode]
    )
  }, [query])

  const getAppointmentModeOptions = (): SelectItem[] =>
    allAppointmentModes.map((appointmentMode) => {
      const appointmentLabel: string =
        appointmentModeValueMapping[appointmentMode.value]

      return {
        label: appointmentLabel,
        value: appointmentMode.id,
        icon: appointmentLabel === 'Video visit' ? 'videovisit' : 'users',
      }
    })

  const onAppointmentModeSelect = (selectedOption: SelectItem) => {
    const { value, label } = selectedOption
    let sortFilter = {}

    if (label === appointmentModeValueMapping['In Person']) {
      sortFilter = { orderBy: SortFilterKeys.DISTANCE }
    }

    modifyURLQuery(router, {
      availabilityType: [value],
      offset: '0',
      ...sortFilter,
    })
    setShowDropdown(false)
  }

  const setAllProviders = () => {
    const queryPathArr = ['availabilityType[]']

    if (selectedAppointmentMode === appointmentModeValueMapping['In Person']) {
      queryPathArr.push('orderBy')
    }

    const formattedQuery = _omit(query, queryPathArr)
    modifyURLQuery(router, formattedQuery, true)

    if (showDropdown) {
      setShowDropdown(false)
    }
  }

  return (
    <FilterDropdownWrapper
      placeholder="Visit type"
      onPillClick={(shouldShow) => {
        if (isMobileScreen || isLargeSize) {
          openMobileFilter(ProviderFilterTypes.AVAILABILITY_TYPE)
        } else {
          setShowDropdown(shouldShow)
        }
      }}
      value={selectedAppointmentMode}
      showDropdown={showDropdown}
      onClearValue={setAllProviders}
      labelClass={styles.label}
      className="mr-2"
    >
      {showDropdown && (
        <SingleSelectDropdown
          options={getAppointmentModeOptions()}
          onSelectItem={onAppointmentModeSelect}
        />
      )}
    </FilterDropdownWrapper>
  )
}

export default AppointmentModeFilter
